/**
 * Legacy exports for backwards compatibility
 * These functions were previously exported from ChallengeInfo.tsx
 * and may be used in other parts of the codebase
 *
 * @deprecated Use the new component-based approach instead
 */

import React from 'react'
import { ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import { t } from 'ttag'
import { formatThousands } from '../../common/helpers/translations'
import { unitsCountPointsShortFunc } from '../../common/helpers/units'
import { CustomTypeCategory, getCustomTypeTextById, getCustomUnitText } from '../../services/CustomTypes'
import { PointsLogic } from '../ChallengeAdmin/components/ChallengePoints/ChallengePoints'
import { isSimpleDayType } from '../../common/helpers/challenges'
import { YumActivityIcon } from '../../components/YumActivityIcon/YumActivityIcon'
import { dailyMaxLabel, totalMaxLabel } from './ChallengeInfo.helpers'

/**
 * @deprecated Use ChallengeInfoListItem component instead
 *
 * Factory function that creates a list item renderer
 * Used for rendering challenge info items in a list
 */
export const getListItems =
  (classes: any, isPoints?: boolean) =>
  (
    {
      label,
      content,
      Icon,
    }: {
      label: string | JSX.Element
      content: any
      Icon?: any
    },
    index: number,
  ) => {
    const listItemClass = [classes.listItem]
    if (isPoints) listItemClass.push(classes.listItemDense)

    return (
      <ListItem className={listItemClass.join(' ')} key={index}>
        {Icon && <ListItemIcon>{Icon}</ListItemIcon>}
        <ListItemText
          className={classes.listItemText}
          primary={label}
          secondary={content}
          primaryTypographyProps={{
            className: classes.listItemTextPrimary,
          }}
          secondaryTypographyProps={{
            className: classes.listItemTextSecondary,
            component: 'div',
          }}
        />
      </ListItem>
    )
  }

/**
 * @deprecated Use PointsFormula component instead
 *
 * Renders a single points activity row for the points formula display
 * Shows how activity values convert to points with optional max limits
 */
export const renderPointsActivity = (roundDailyPoints: boolean, isDayView?: boolean) => (activity: PointsLogic) => {
  // Check activity category types
  const isCustom = [
    CustomTypeCategory.CUSTOM_SIMPLE,
    CustomTypeCategory.CUSTOM_WITH_AMOUNT,
    CustomTypeCategory.BONUS_SIMPLE,
    CustomTypeCategory.BONUS_WITH_AMOUNT,
  ].includes(activity.category)
  const isBonusPoints = [CustomTypeCategory.BONUS_SIMPLE, CustomTypeCategory.BONUS_WITH_AMOUNT].includes(
    activity.category,
  )
  const isCustomSimple = [CustomTypeCategory.CUSTOM_SIMPLE, CustomTypeCategory.BONUS_SIMPLE].includes(activity.category)

  // Format value and determine unit
  const value = formatThousands(isDayView ? activity.dayValue : activity.value)
  const unit = isCustom
    ? isCustomSimple
      ? t`Done`.toLowerCase()
      : activity.customUnitName
    : getCustomUnitText(activity.unit)

  // Get points from appropriate source
  let points = isDayView ? activity.dayPoints : activity.points
  let dailyMaxExceeded = false
  let totalMaxExceeded = false

  if (isDayView && activity.dayData) {
    points = activity.dayData.points
    dailyMaxExceeded = activity.dayData.dailyMaxExceeded === true
    totalMaxExceeded = activity.dayData.totalMaxExceeded === true
  }

  const pointsAmount = formatThousands(points)
  const pointsUnit = unitsCountPointsShortFunc(points)

  // Calculate daily max
  let dailyMax = !activity.dailyGoal ? 0 : (activity.dailyGoal * activity.points) / activity.value
  if (roundDailyPoints) dailyMax = Math.floor(dailyMax)
  const maxPointsUnit = unitsCountPointsShortFunc(dailyMax)
  const showDailyMax =
    activity.dailyGoal &&
    activity.category !== CustomTypeCategory.CUSTOM_SIMPLE &&
    !isSimpleDayType(activity.category) &&
    (!isDayView || dailyMaxExceeded)

  // Calculate total max
  const totalMax = !activity.totalMax ? 0 : (activity.totalMax * activity.points) / activity.value
  const totalMaxUnit = unitsCountPointsShortFunc(totalMax)
  const showTotalMax = totalMax && totalMax > 0 && (!isDayView || totalMaxExceeded)

  return {
    label: (
      <>
        {(isCustom ? activity.customActivityName : getCustomTypeTextById(activity.category)) as string}
        {isBonusPoints ? (
          <span style={{ fontSize: '11px', fontWeight: 400 }}> ({t`Bonus points`.toLowerCase()})</span>
        ) : null}
      </>
    ),
    content: (
      <>
        <div style={{ marginBottom: '2px' }}>{`${value} ${unit} = ${pointsAmount} ${pointsUnit}`}</div>
        {showDailyMax ? (
          <div style={{ opacity: 0.7, fontSize: '11px' }}>
            {`${dailyMaxLabel}: ${formatThousands(dailyMax)} ${maxPointsUnit}${dailyMaxExceeded ? ' (' + t`exceeded` + ')' : ''}`}
          </div>
        ) : null}
        {showTotalMax ? (
          <div style={{ opacity: 0.7, fontSize: '11px' }}>
            {`${totalMaxLabel}: ${formatThousands(totalMax)} ${totalMaxUnit}${totalMaxExceeded ? ' (' + t`exceeded` + ')' : ''}`}
          </div>
        ) : null}
      </>
    ),
    Icon: (
      <YumActivityIcon
        category={activity.icon || activity.category}
        color={'darkBlue'}
        isInactive={true}
        extraPadding={true}
      />
    ),
  }
}
